import type {
  ColumnFilterConfig,
  Header,
  SortDirection,
} from '@tanstack/react-table';

import type { IconName } from '../icons';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { PopoverClose } from '@radix-ui/react-popover';
import { flexRender } from '@tanstack/react-table';
import { useState } from 'react';

import {
  Button,
  Checkbox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '../../ui';
import { TextInput } from '../form';
import { Icon } from '../icons';

type FilterFieldProps = {
  columnValues: string[];
  filter: ColumnFilterConfig;
  isFilterActive: boolean;
  onChange: (value?: string[] | string) => void;
  value: string[] | string | undefined;
};

const FilterField = ({
  columnValues,
  filter,
  isFilterActive,
  onChange,
  value,
}: FilterFieldProps) => {
  const [search, setSearch] = useState<string>('');
  if (filter.type === 'select') {
    const filterValue = Array.isArray(value) ? value : [];

    const filterOptions =
      filter.options ??
      columnValues
        .filter((value) => !!value)
        .map((value) => ({
          label: filter.optionsMap?.[value]?.label ?? value,
          value,
        }));

    const filteredOptions =
      filter.searchable && search.length > 1
        ? filterOptions.filter((option) =>
            option.label.toLowerCase().includes(search.toLowerCase())
          )
        : filterOptions;

    return (
      <div className="space-y-3">
        {!!filter.searchable && (
          <>
            <TextInput
              className="w-full focus-visible:outline-none focus-visible:ring-0"
              leftIcon="search"
              placeholder={filter.searchablePlaceholder || 'Rechercher'}
              rightIcon="delete"
              value={search}
              onChange={({ target: { value } }) => {
                setSearch(value);
              }}
              onRightIconClick={() => setSearch('')}
            />
            <Separator className="bg-neutral-200" />
          </>
        )}

        <div
          className={cn('space-y-3', {
            'max-h-40 overflow-y-auto': filter.searchable,
          })}
        >
          {filteredOptions.map((option) => {
            const optionIcon = filter.optionsMap?.[option.value]?.icon;

            return (
              <div
                key={`option_${option.value}`}
                className="flex max-h-10 items-center gap-1.5"
              >
                <Checkbox
                  checked={value?.includes(option.value) ?? false}
                  data-testid={`filter-option-${option.value}`}
                  id={option.value}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      return onChange([...filterValue, option.value]);
                    }
                    return onChange(
                      filterValue.filter((val) => val !== option.value)
                    );
                  }}
                />
                <Label
                  className="flex items-center gap-1"
                  htmlFor={option.value}
                >
                  {!!optionIcon && <Icon name={optionIcon} />}
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (filter.type === 'text') {
    return (
      <div className="flex items-center gap-2">
        <TextInput
          leftIcon="search"
          value={value}
          onChange={({ target: { value } }) => onChange(value)}
        />
        <PopoverClose asChild>
          <Button
            className="cursor-pointer"
            disabled={!isFilterActive}
            title="Supprimer le filtre"
            variant="ghost"
            onClick={() => onChange(undefined)}
          >
            <Icon name="delete" />
          </Button>
        </PopoverClose>
      </div>
    );
  }

  throw new Error('Unknown filter type');
};

export const HeaderCell = <TData,>({
  header,
}: {
  header: Header<TData, unknown>;
}) => {
  const { column, getContext, isPlaceholder } = header;

  const renderer = () =>
    isPlaceholder ? null : flexRender(column.columnDef.header, getContext());

  const sortingIcons: Record<SortDirection | 'default', IconName> = {
    asc: 'chevron-up',
    desc: 'chevron-down',
    default: 'chevrons-up-down',
  };

  if (column.columnDef.enableSorting) {
    const sortDirection = column.getIsSorted();
    const iconName = sortDirection
      ? sortingIcons[sortDirection]
      : sortingIcons.default;
    const sortLabels: Record<SortDirection, string> = {
      asc: 'sorted ascending',
      desc: 'sorted descending',
    };
    const sortLabel = sortDirection ? sortLabels[sortDirection] : 'not sorted';
    return (
      <Button
        aria-label={`Sort by ${String(column.columnDef.header ?? column.id)}, ${sortLabel}`}
        variant="ghost"
        onClick={() => column.toggleSorting(sortDirection === 'asc')}
      >
        {renderer()}
        <Icon name={iconName} />
      </Button>
    );
  }

  if (column.columnDef.meta?.filter) {
    const rawFilterValue = column.getFilterValue();
    const filterValue =
      Array.isArray(rawFilterValue) || typeof rawFilterValue === 'string'
        ? rawFilterValue
        : undefined;
    const hasFilterValues = Array.isArray(rawFilterValue)
      ? rawFilterValue.length > 0
      : rawFilterValue !== undefined;
    const isFilterActive = column.getIsFiltered() && hasFilterValues;
    const columnValues = Array.from(column.getFacetedUniqueValues().keys());

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost">
            {renderer()}
            <Icon
              className={cn({
                'fill-primary text-primary': isFilterActive,
              })}
              name="filter"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="lg:w-auto">
          <FilterField
            columnValues={columnValues}
            filter={column.columnDef.meta.filter}
            isFilterActive={isFilterActive}
            value={filterValue}
            onChange={(value) => column.setFilterValue(value)}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return <span className="px-4 py-2">{renderer()}</span>;
};
